import React, { useState, useEffect } from 'react';
import { userService } from '../services';
import { DataTable } from '../components';
import { useSideDialog, useModalDialog, useGlobalState, useMessage } from '../hooks';
import { handleError, Role, authMethod } from '../helpers';

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    const promise = new Promise((resolve, reject) => {
      userService
        .getAll()
        .then(res => {
          setUsers([...res.users]);
          setLoading(false);
          resolve(res.users);
        })
        .catch(err => {
          setLoading(false);
          showMessage(err, 'error');
        });
    });

    Promise.all([promise, fetchOwnedItemsCount()])
      .then(result => {
        updateOwnedItemsCount(result[0], result[1]);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchOwnedItemsCount = async () => {
    return new Promise((resolve, reject) => {
      userService
        .getOwnedItemsTotalCount()
        .then(res => {
          resolve(res.owned_items_count);
        })
        .catch(err => {
          return null;
        });
    });
  };

  const updateOwnedItemsCount = (data, statistics) => {
    let dataTmp = data;
    let statistic = {};
    let tmp = null;

    let maxValue = 0;

    statistics.forEach(row => {
      if (row.owned_items_count > maxValue) {
        maxValue = row.owned_items_count;
      }
    });

    setUsers(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.user_id === item.user_id);
        return {
          ...item,
          owned_items_count: statistic ? statistic.owned_items_count : 0,
          owned_items_count_max: maxValue,
          attribute_ratio: statistic ? statistic.attribute_ratio : null,
        };
      })
    );
  };

  const addUser = async data => {
    const users = { users: Array.isArray(data) ? data : [data] };

    await userService
      .create(users)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message, res.warning ? 'warning' : undefined);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editUser = async data => {
    const users = { users: Array.isArray(data) ? data : [data] };

    await userService
      .update(users)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteUser = async data => {
    await userService
      .destroy({ users: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  const userAuthMethodValues = Object.entries(authMethod).reduce(
    (a, [key, value]) => ({ ...a, [key]: { label: value } }),
    {}
  );
  const userIsEnabledValues = {
    true: { label: 'Enabled', className: 'pill' },
    false: { label: 'Disabled', className: 'pill pill-semifull-red' },
  };

  return (
    <div className="Users wide-margin">
      <DataTable
        columns={[
          {
            id: 'user_fullname',
            name: '',
            type: 'owner',
            link: '/users/:user_id',
            data: { photo: 'user_photo' },
          },
          { id: 'user_fullname', name: 'Name', link: '/users/:user_id', className: 'bold' },
          { id: 'user_username', name: 'Username', className: 'small-text' },
          { id: 'user_title', name: 'Title', viewMode: 'descriptions' },
          { id: 'user_department', name: 'Department', viewMode: 'descriptions' },
          {
            id: 'owned_items_count',
            name: 'Assigned Assets',
            type: 'chart',
            chartType: 'bar',
            data: { data: 'owned_items_count', max: 'owned_items_count_max' },
            viewMode: 'statistics',
          },
          {
            id: 'attribute_ratio',
            name: 'Documentation Level',
            type: 'chart',
            chartType: 'percent-bar',
            data: { data: 'attribute_ratio' },
            viewMode: 'statistics',
          },
          {
            id: 'user_last_login_timestamp',
            name: 'Last Login',
            type: 'datetime',
            viewMode: 'statistics',
          },
          { id: 'user_role_name', name: 'Role' },
          {
            id: 'user_auth_method',
            name: 'Type',
            value: 'user_auth_method',
            values: userAuthMethodValues,
            className: 'small-text',
          },
          { id: 'user_is_enabled', name: 'Status', type: 'boolean', values: userIsEnabledValues },
        ]}
        buttons={[
          {
            label: '+ Add',
            action: 'add',
            mainButton: true,
            onClick: items => showSideDialog('addUser', items || selectedItems, addUser),
          },
          {
            label: 'Edit',
            action: 'edit',
            onClick: items => showSideDialog('editUser', items || selectedItems, editUser),
          },
          {
            label: 'Delete',
            action: 'delete',
            onClick: items => showModalDialog('deleteUser', items || selectedItems, deleteUser),
          },
        ]}
        data={users}
        title="Users"
        idColumn="user_id"
        primaryColumn="user_fullname"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        editable={editable}
        filterable={true}
        showViewModes={true}
        filterObjects={[
          'user_fullname',
          'user_username',
          'user_email',
          'user_title',
          'user_department',
          'user_role_name',
          'user_auth_method',
          'user_is_enabled',
        ]}
        filterObjectsValueMap={{
          user_auth_method: userAuthMethodValues,
          user_is_enabled: userIsEnabledValues,
        }}
      />
    </div>
  );
};
